import {CrewBankingDetailsRecord} from '../core/_requests'

export const IFSC_RTGS_LENGTH = 11
export const IBAN_MIN_LENGTH = 15
export const IBAN_MAX_LENGTH = 34

export type BankCodeType = 'IFSC_RTGS' | 'SWIFT_IBAN'

export type BankingDetailsForm = {
  nameOfBeneficiary: string
  bankName: string
  accountNumber: string
  branchName: string
  branchCode: string
  bankCodeType: BankCodeType
  bankCodeValue: string
  bankAddress: string
  accountCurrency: 'USD' | 'EUR' | 'GBP' | 'SGD' | 'INR' | 'AED' | 'OTHER'
  otherCurrency: string
  remarks: string
}

export type BankingDetailsFormErrors = Partial<Record<keyof BankingDetailsForm, string>>

export const createEmptyBankingDetailsForm = (): BankingDetailsForm => ({
  nameOfBeneficiary: '',
  bankName: '',
  accountNumber: '',
  branchName: '',
  branchCode: '',
  bankCodeType: 'IFSC_RTGS',
  bankCodeValue: '',
  bankAddress: '',
  accountCurrency: 'USD',
  otherCurrency: '',
  remarks: '',
})

export const toBankingDetailsForm = (
  account?: CrewBankingDetailsRecord | null
): BankingDetailsForm => {
  const normalizedType = (account?.ifscRtgsType || '').toString().trim().toUpperCase()
  const accountCurrency = (account?.accountCurrency || account?.salaryCurrency || 'USD')
    .toString()
    .trim()
    .toUpperCase()
  const bankCodeType: BankCodeType =
    normalizedType === 'RTGS' || normalizedType === 'IFSC' || (account?.ifscRtgsCode || '').trim()
      ? 'IFSC_RTGS'
      : 'SWIFT_IBAN'

  return {
    nameOfBeneficiary: (account?.nameOfBeneficiary || '').toString(),
    bankName: (account?.bankName || '').toString(),
    accountNumber: (account?.accountNumber || '').toString(),
    branchName: (account?.branchName || '').toString(),
    branchCode: (account?.branchCode || '').toString(),
    bankCodeType,
    bankCodeValue:
      bankCodeType === 'IFSC_RTGS'
        ? (account?.ifscRtgsCode || '').toString()
        : (account?.swiftIandi || '').toString(),
    bankAddress: (account?.bankAddress || '').toString(),
    accountCurrency:
      accountCurrency === 'EUR' ||
      accountCurrency === 'GBP' ||
      accountCurrency === 'SGD' ||
      accountCurrency === 'INR' ||
      accountCurrency === 'AED' ||
      accountCurrency === 'OTHER'
        ? accountCurrency
        : 'USD',
    otherCurrency: (account?.otherCurrency || account?.salaryOtherCurrency || '').toString(),
    remarks: (account?.remarks || '').toString(),
  }
}

export const validateBankingDetailsForm = (form: BankingDetailsForm): BankingDetailsFormErrors => {
  const errors: BankingDetailsFormErrors = {}

  if (!form.nameOfBeneficiary.trim()) {
    errors.nameOfBeneficiary = 'Beneficiary is required.'
  }
  if (!form.bankName.trim()) {
    errors.bankName = 'Bank is required.'
  }
  if (!form.accountNumber.trim()) {
    errors.accountNumber = 'Account number is required.'
  } else if (!/^[a-zA-Z0-9]{8,20}$/.test(form.accountNumber.trim())) {
    errors.accountNumber = 'Account Number must be 8-20 alphanumeric characters'
  }
  if (!form.branchName.trim()) {
    errors.branchName = 'Branch name is required.'
  }
  if (!form.branchCode.trim()) {
    errors.branchCode = 'Branch code is required.'
  } else if (!/^[a-zA-Z0-9\-]{2,10}$/.test(form.branchCode.trim())) {
    errors.branchCode = 'Branch Code must be 2-10 alphanumeric characters or hyphens'
  }
  if (!form.bankCodeValue.trim()) {
    errors.bankCodeValue =
      form.bankCodeType === 'IFSC_RTGS'
        ? 'IFSC / RTGS Code is required.'
        : 'SWIFT / IBAN Code is required.'
    return errors
  }

  const normalizedBankCode = form.bankCodeValue.replace(/\s+/g, '').toUpperCase()
  if (form.bankCodeType === 'IFSC_RTGS') {
    const isIfscRtgs = new RegExp(`^[A-Z0-9]{${IFSC_RTGS_LENGTH}}$`).test(normalizedBankCode)
    if (!isIfscRtgs) {
      errors.bankCodeValue = `IFSC / RTGS Code must be ${IFSC_RTGS_LENGTH} alphanumeric characters`
    }
  } else {
    const isSwift = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(normalizedBankCode)
    const isIban = new RegExp(`^[A-Z0-9]{${IBAN_MIN_LENGTH},${IBAN_MAX_LENGTH}}$`).test(
      normalizedBankCode
    )
    if (!(isSwift || isIban)) {
      errors.bankCodeValue = `SWIFT / IBAN Code must be 8 or 11 characters (SWIFT) or ${IBAN_MIN_LENGTH}-${IBAN_MAX_LENGTH} characters (IBAN)`
    }
  }

  if (!form.bankAddress.trim()) {
    errors.bankAddress = 'Bank address is required.'
  }
  if (!form.accountCurrency) {
    errors.accountCurrency = 'Account currency is required.'
  }
  if (form.accountCurrency === 'OTHER' && !form.otherCurrency.trim()) {
    errors.otherCurrency = 'Other currency is required when account currency is OTHER.'
  } else if (
    form.accountCurrency === 'OTHER' &&
    !/^[A-Z]{3}$/.test(form.otherCurrency.trim().toUpperCase())
  ) {
    errors.otherCurrency = 'Currency Code must be 3 uppercase letters'
  }

  return errors
}

export const toBankingDetailsPayload = (form: BankingDetailsForm): CrewBankingDetailsRecord => {
  const useIfscRtgs = form.bankCodeType === 'IFSC_RTGS'
  return {
    nameOfBeneficiary: form.nameOfBeneficiary.trim(),
    bankName: form.bankName.trim(),
    accountNumber: form.accountNumber.trim(),
    branchName: form.branchName.trim(),
    branchCode: form.branchCode.trim(),
    ifscRtgsType: useIfscRtgs ? 'RTGS' : 'IFSC',
    ifscRtgsCode: useIfscRtgs ? form.bankCodeValue.trim() : '',
    swiftIandi: useIfscRtgs ? '' : form.bankCodeValue.trim(),
    bankAddress: form.bankAddress.trim(),
    accountCurrency: form.accountCurrency,
    otherCurrency: form.accountCurrency === 'OTHER' ? form.otherCurrency.trim() : '',
    remarks: form.remarks.trim(),
  }
}
